import { IJob } from '@medely/types';
import { ShiftRequest, ShiftRequestKinds, ShiftRequestStatus } from '@mp/graphql/generated/graphql';
import { jobBonusCents, PayoutKind } from '@mp/utils';

interface Props {
  job: IJob;
  isMarketplace: boolean;
  isFgEnabled: boolean;
}

export const useRateRequest = ({ job, isMarketplace, isFgEnabled }: Props) => {
  const jobProfileForAccount = job.job_profile_for_account_id;
  const isJobActiveAndTen99 =
    job.status === 'active' && jobProfileForAccount?.payout_kind === PayoutKind.Ten99;

  // @ts-ignore - until we add codegen to job query
  const isRateNegotiationAllowedForJob = job.location.state?.allow_rate_negotiation ?? false;

  const isRateRequestEnabled =
    isMarketplace && isJobActiveAndTen99 && isFgEnabled && isRateNegotiationAllowedForJob;

  const jobBonusPayRate = jobBonusCents(job);
  const currentTotalJobPayRateCents =
    (job.job_billing_sums?.payout_total_amount_cents ?? 0) + jobBonusPayRate;

  // @ts-ignore - until we add codegen to job query
  const rateChangeRequests: ShiftRequest[] | undefined = job?.professional_shift_requests?.filter(
    (r: ShiftRequest) => r.request_kind === ShiftRequestKinds.RateChange,
  );

  const mostRecentRateRequest: ShiftRequest | undefined = [...(rateChangeRequests ?? [])]
    ?.sort(
      (a: ShiftRequest, b: ShiftRequest) =>
        new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime(),
    )
    ?.shift();

  const isMostRecentRateRequestApproved =
    mostRecentRateRequest?.status === ShiftRequestStatus.Approved;
  const isCurrentTotalJobPayRateGreaterThanRequestedRate =
    currentTotalJobPayRateCents >=
    (mostRecentRateRequest?.transformation_data?.requested_payout_total_cents ?? 0);

  const hasApprovedRateRequest =
    isMostRecentRateRequestApproved && isCurrentTotalJobPayRateGreaterThanRequestedRate;
  const hasPendingRequest = mostRecentRateRequest?.status === ShiftRequestStatus.Pending;

  const hasReachedMaxRequests = (rateChangeRequests?.length ?? 0) >= 2;

  const shouldDisplayApprovedCurrency = isRateRequestEnabled && hasApprovedRateRequest;

  const shouldDisplayAdditionalRateOptionsButton =
    isRateRequestEnabled && !hasApprovedRateRequest && !hasReachedMaxRequests;
  const isAdditionalRateOptionsButtonDisabled = hasPendingRequest;

  const shouldDisplayRateRequestBanner =
    isRateRequestEnabled &&
    (isMostRecentRateRequestApproved && !isCurrentTotalJobPayRateGreaterThanRequestedRate
      ? false
      : !!mostRecentRateRequest);
  const shouldDisplayRateRequestBadge = shouldDisplayRateRequestBanner;

  return {
    mostRecentRateRequest,
    shouldDisplayAdditionalRateOptionsButton,
    oldRateCents: mostRecentRateRequest?.transformation_data?.original_payout_total_cents ?? 0,
    newRateCents: currentTotalJobPayRateCents,
    shouldDisplayApprovedCurrency,
    shouldDisplayRateRequestBanner,
    shouldDisplayRateRequestBadge,
    isAdditionalRateOptionsButtonDisabled,
  };
};
